import React, { useState } from "react";
import { Globe } from "lucide-react";
import toast from "react-hot-toast";

/**
 * Checkbox shown on sold property detail pages so agents can mark (or update)
 * whether the sale was attributed to the Collings buyer portal.
 *
 * Used by the REA/Collings + public property pages. The CRM property page uses
 * `PortalAttributionPicker` instead, where picking a category tags the sale.
 */
const SoldThroughPortalToggle = ({
    checked = false,
    onToggle,
    disabled = false,
}) => {
    const [saving, setSaving] = useState(false);

    const handleChange = async (e) => {
        const nextValue = e.target.checked;
        if (typeof onToggle !== "function") return;
        try {
            setSaving(true);
            await onToggle(nextValue);
        } catch (err) {
            console.error("Failed to update portal attribution:", err);
            toast.error("Failed to update portal attribution");
        } finally {
            setSaving(false);
        }
    };

    return (
        <label
            htmlFor="sold-through-portal-toggle"
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer select-none transition-colors ${
                checked
                    ? "bg-teal-50 border-teal-200"
                    : "bg-gray-50 border-gray-100 hover:border-gray-200"
            } ${disabled || saving ? "opacity-60 cursor-not-allowed" : ""}`}
        >
            <input
                id="sold-through-portal-toggle"
                type="checkbox"
                checked={Boolean(checked)}
                onChange={handleChange}
                disabled={disabled || saving}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
            />
            <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                    <Globe className="w-3.5 h-3.5 text-[#55C5D0] flex-shrink-0" />
                    Sold through the Collings portal
                </span>
                <span className="block text-xs font-medium text-gray-400 mt-0.5">
                    {saving
                        ? "Saving..."
                        : "Track sales attributed to the Collings buyer portal for platform analytics."}
                </span>
            </span>
        </label>
    );
};

export default SoldThroughPortalToggle;
